// [PURPOSE]: Settings Context - Centralized user preferences and configuration
// [WHY]: Extract settings-related state from monolithic App.jsx (16,437 lines)
// [INTEGRATION]: Provides user settings, preferences, and configuration via React Context
// [CREATED]: October 24, 2025 - Phase 4A: Architectural Refactoring

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { safeLocalStorageSet } from '../utils/storageValidator';
import { DISPLAY_MODES } from '../constants/enums';
// [PHASE B / #068 — 2026-05-07]: Single source of truth for the dev-build
//   gate. Closes COMING_SOON_ROADMAP §6.1.
import { IS_DEV_BUILD } from '../utils/devMode';

/**
 * SettingsContext provides all user settings and preferences
 *
 * This context manages:
 * - General settings (sell ratio, currency weight, premium status)
 * - Inventory settings (containers, auto-purchase, grid)
 * - Equipment settings (slot mode, weight display, quick equip)
 * - Mobile settings (interaction mode)
 * - Display modes (default, simple, just inventory)
 * - Quick actions settings
 * - Inspiration settings
 * - Tutorial settings
 * - Debug mode
 * - Saved characters and session info
 */
export const SettingsContext = createContext(null);

// [FIX APR 2026]: Extracted DEFAULT_SETTINGS so both useState init and resetSettings use the same source of truth
// [WHY]: resetSettings was missing 5 keys and had a wrong autosaveInterval (30000 vs 60000)
function getDefaultSettings() {
  const isMobileDevice = window.innerWidth < 768;
  return {
    // General Settings
    defaultSellRatio: 0.5,
    currencyHasWeight: true,
    isPremium: false,
    allowContainersInContainers: false,
    autoPurchaseItems: true,
    useVaultGoldForPurchases: false,  // [FIX NOV 22, 2025]: Allow using vault gold for purchases
    rememberLastCharacter: true,

    // Autosave Settings
    autosaveInterval: 60000, // 1 minute (user-configurable)
    autosaveEnabled: true,

    // Inventory Settings
    autoOpenContainers: false,  // Don't auto-open containers when items are dropped
    useCustomGrid: false,  // false = strength-based (default), true = custom size

    // Spell Settings
    classDependentSpellbook: true,  // Only show spells for current class
    enforceComponentConsumption: true,  // Consume material components marked `consumed: true` on cast

    // Input Settings
    // [F-002 FIX 2026-05-09]: Default to ON. Off-by-default made 9 documented
    // shortcuts (H/D/L/E/S/I/R/J/?/Esc) silently no-op for every new user. The
    // `?` modal makes them discoverable. Users who dislike single-key hotkeys
    // can still toggle off in Settings → Input.
    keyboardShortcutsEnabled: true,

    // Rules Settings
    // [BREADCRUMB]: Global edition sub-axis (2014/2024). The endgame-correct location is
    //   per-character `character.basics.ruleSystem` (RSE-MVP Stage 5); this global preset
    //   is a convenience that RSE will reconcile. See master-queue 2M-edition-rse-reconcile.
    ruleEdition: '5e-2014',  // '5e-2014' = PHB 2014 defaults, '5e-2024' = PHB 2024 / 5.5e defaults
    exhaustionRules: '5e',  // '5e' = 2014 rules (6 levels), '5.5e' = 2024 rules (10 levels with d20 penalty)
    deathSavePersistence: 'reset_on_long_rest',  // 'reset_on_long_rest' = RAW (PHB p.197), 'persist_until_stabilize' = variant rule
    // [AUDIT 2026-05-17 #51 / B.5 C3]: CPI/SPI drawer is opt-in. Default OFF.
    //   When false (default): MonsterEditor hides the CPI drawer + toggle; Combat
    //   Manager uses DMG XP-budget RAW difficulty (DMG p.82-83). When true: drawer
    //   is available + Combat Manager uses CPI ratio bands as the difficulty
    //   readout. CPI is the cross-system overlay; CR/XP is the RAW default.
    //   See feedback_cr_default_cpi_opt_in.md + reference_dmg_monster_design_facts.md.
    enableCPIDrawer: false,
    // [Spec 1b — Phase B / #335]: PHB p.144 armor-proficiency penalties.
    // When true: non-proficient armor → disadvantage on STR/DEX checks/saves/attacks
    // + cannot cast spells. Defaults ON per user directive 2026-05-12 (app has no
    // active users yet, so backward compat is moot for fresh installs). For loaded
    // characters that already have non-proficient armor equipped, the load-time
    // detector (Task 18) surfaces a warning toast offering to toggle off.
    enableArmorProficiencyPenalties: true,

    // [Sub-plan 4M — 2026-05-28]: Off-list scroll casting house rule (DMG p.139).
    // RAW: spell scrolls are unintelligible if the spell is not on your class list.
    // House rule: enable an Arcana check (DC 10 + spell level) to attempt off-list
    // casts. Default OFF (RAW). On failure: scroll consumed + spell fizzles.
    enableOffListCasting: false,

    // Bastion Settings
    enableBastionUpgrades: false,  // [PHASE 6]: Homebrew facility upgrades (Level 1→2→3) - Default OFF per DMG 2024
    bastionUnlockLevel: 5,  // [v1.1]: D&D 2024 default — bastions unlock at level 5. Set to 1 for homebrew early unlock.
    // [2026-05-09]: Party Bastion sizing mode.
    //   'memberCount' (default) — grid size + slot cap scale with the number of
    //     party members excluding the DM. More players = bigger shared stronghold.
    //   'custom' — DM picks an explicit tier from the create form; size doesn't auto-scale.
    partyBastionSizeMode: 'memberCount',

    // [FIX NOV 27, 2025]: Crafting/Downtime Settings
    craftingRuleset: 'phb',  // 'phb' = PHB basic rules, 'xgte' = Xanathar's expanded rules (helpers, complications)
    disableCraftingToolRequirements: false,  // If true, skip tool proficiency checks (DM discretion)

    // Equipment Settings
    equipmentSlotMode: 'standard', // standard, simplified, realistic
    showEquipmentWeight: true,
    enforceSlotLimits: true,
    quickEquipEnabled: true,
    visualEquipmentDisplay: false,

    // Mobile Settings
    mobileInteractionMode: isMobileDevice ? 'tap' : 'drag',  // 'drag' or 'tap'

    // Display Mode Settings
    displayMode: 'default',  // 'default' = full features, 'simple' = streamlined, 'justInventory' = minimal
    // [S3 REMOVED]: simpleUI default dropped — use displayMode. Legacy values still migrated below.

    // Quick Actions Settings
    quickActionsPosition: 'right',  // 'left' or 'right' (legacy - now uses draggable position)
    quickActionsTabPosition: null,  // {x, y} for draggable tab positioning
    showQuickActions: true,  // Enable/disable quick actions bar
    // [P1.7 APR 2026]: Per-action visibility for the Quick Actions bar.
    // [WHY]: Restores capability that existed before the bar was archived.
    //   User can now hide individual saves, skills, hit dice, etc.
    // [BACKWARDS COMPAT]: All flags default true. Existing users keep
    //   their full bar; the QuickActionsBar guards tolerate undefined.
    quickActionsVisibility: {
      initiative: true,
      combatToggles: true,
      saves: { str: true, dex: true, con: true, int: true, wis: true, cha: true },
      skills: {
        Perception: true, Investigation: true, Stealth: true,
        Athletics: true, Acrobatics: true, Insight: true
      },
      hitDice: true,
      quickDice: true
    },

    // Inspiration Settings
    inspirationAutoPrompt: true,  // Automatically prompt to use inspiration on failed rolls
    inspirationThreshold: 3,  // Maximum failure margin to offer inspiration (1-5)
    maxInspiration: 1,  // PHB 2024 RAW: "Only One at a Time" (Heroic Inspiration cap). Slider 1-10 supports homebrew override.

    // Tutorial Settings
    hideTutorials: false,  // Hide all tutorial/info boxes globally

    // Tab Visibility Settings [FIX JAN 2026 - Issue #5]
    hiddenTabs: [],  // Array of tab names to hide from navigation

    // [A17 — 2026-05-25]: Roster tab is hidden by default until the feature
    // is finished. User report 5/17 — "Roster tab unsolicited + disconnected
    // + broken — hide behind flag". MobileNavigation filters the 'roster'
    // entry out of allTabs when this is false; App.jsx gates the
    // `activeTab === 'roster'` render block on the same flag. Power users
    // can opt in via the toggle in Settings → Display → Tab Visibility.
    enableRosterTab: false,

    // Accessibility Settings
    // [A11Y — 2026-06-20]: Opt-in dyslexia / accessibility overlay.
    // AppearanceController reads these and sets data-a11y tokens on <html>.
    // themes.css overrides are INERT when this object has all-false values.
    // Save/load parity: every field here has a corresponding restore path via
    // the { ...defaults, ...parsed } merge in the useState initializer above.
    accessibility: {
      dyslexiaFont: false,    // legible font stack + generous spacing
      largeText: false,       // bump root font-size (18px from 16px)
      highContrast: false,    // brighter text + stronger borders
      reducedMotion: false    // disable transitions / animations
    },

    // Theme Settings
    // [DESIGN-THEME-SUBSTRATE — 2026-07-04]: Opt-in alternate theme selection.
    // AppearanceController reads this and sets/removes data-theme on <html>.
    // '' = default dark (no attribute) — the current look, completely inert
    // until a theme is authored into src/themes.css (see src/utils/themeRegistry.js
    // for the bring-back contract) and appended to AVAILABLE_THEMES.
    // Save/load parity: this field is restored via the { ...defaults, ...parsed }
    // merge in the useState initializer below, same as every other setting.
    theme: '',

    // Debug Settings
    // [PHASE B / #068 — 2026-05-07]: Routed through `IS_DEV_BUILD`
    //   so the dev-build expression has a single source of truth.
    debugMode: IS_DEV_BUILD,
    showDebugTools: false,
    // [SUB-PLAN 1B-i — 2026-05-23]: Re-enables the LEGACY inline custom-feat
    //   creator (src/components/legacy/FeatsPanelInlineCreator.js) inside the
    //   Feats tab. Default OFF — the Workshop-hosted CustomFeatCreator is the
    //   user-facing path. Flipping this ON exposes the pre-Workshop modal for
    //   parity checks / debugging. Dev-only surface (toggle is gated on
    //   NODE_ENV=development in SettingsPanel).
    showLegacyFeatInlineCreator: false
  };
}

export const SettingsProvider = ({ children }) => {
  // ============================================================================
  // CORE SETTINGS STATE (with localStorage persistence)
  // ============================================================================

  const [settings, setSettings] = useState(() => {
    // Load settings from localStorage or use defaults
    const savedSettings = localStorage.getItem('tabletopInventory_settings');
    const defaults = getDefaultSettings();

    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // [FIX - NOV 3 SESSION 5]: Check parsed is not null before spreading
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          console.warn('Invalid settings data in localStorage, using defaults');
          return defaults;
        }
        const merged = { ...defaults, ...parsed };

        // [MIGRATION]: Convert legacy simpleUI to displayMode
        if (parsed.simpleUI !== undefined && !parsed.displayMode) {
          merged.displayMode = parsed.simpleUI ? 'justInventory' : 'default';
        }

        return merged;
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    }

    return defaults;
  });

  // ============================================================================
  // SESSION & CHARACTER STATE
  // ============================================================================

  const [savedCharacters, setSavedCharacters] = useState(() => {
    try {
      const saved = localStorage.getItem('savedCharacters');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());

  // ============================================================================
  // QUICK BONUS PRESET STATE
  // ============================================================================

  const [hiddenPresets, setHiddenPresets] = useState([]);
  const [customBonus, setCustomBonus] = useState({});

  // ============================================================================
  // PERSISTENCE (Save settings to localStorage)
  // ============================================================================

  // Save settings to localStorage whenever they change
  useEffect(() => {
    safeLocalStorageSet('tabletopInventory_settings', JSON.stringify(settings));
  }, [settings]);

  // ============================================================================
  // SETTINGS ACTIONS
  // ============================================================================

  /**
   * Update a single setting
   * @param {string} key - Setting key to update
   * @param {any} value - New value
   */
  const updateSetting = useCallback((key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  /**
   * Update multiple settings at once
   * @param {Object} updates - Object with setting key-value pairs
   */
  const updateSettings = useCallback((updates) => {
    setSettings(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  /**
   * Reset all settings to defaults
   */
  // [FIX APR 2026]: Use getDefaultSettings() to ensure resetSettings matches initial defaults exactly
  const resetSettings = useCallback(() => {
    setSettings(getDefaultSettings());
  }, []);

  /**
   * Toggle a boolean setting
   * @param {string} key - Setting key to toggle
   */
  const toggleSetting = useCallback((key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  /**
   * Get current value of a setting
   * @param {string} key - Setting key
   * @param {any} defaultValue - Default value if key doesn't exist
   * @returns {any} Setting value
   */
  const getSetting = useCallback((key, defaultValue = null) => {
    return settings[key] !== undefined ? settings[key] : defaultValue;
  }, [settings]);

  // ============================================================================
  // DISPLAY MODE HELPERS
  // ============================================================================

  /**
   * Check if currently in simple mode
   * @returns {boolean}
   */
  // [FIX APR 2026 sub-G tail]: DISPLAY_MODES enum in lieu of bare-string compares.
  const isSimpleMode = useCallback(() => {
    return settings.displayMode === DISPLAY_MODES.SIMPLE
        || settings.displayMode === DISPLAY_MODES.JUST_INVENTORY;
  }, [settings.displayMode]);

  /**
   * Check if currently in just inventory mode
   * @returns {boolean}
   */
  const isJustInventoryMode = useCallback(() => {
    return settings.displayMode === DISPLAY_MODES.JUST_INVENTORY;
  }, [settings.displayMode]);

  /**
   * Check if currently in default mode
   * @returns {boolean}
   */
  const isDefaultMode = useCallback(() => {
    return settings.displayMode === DISPLAY_MODES.DEFAULT;
  }, [settings.displayMode]);

  // ============================================================================
  // MOBILE HELPERS
  // ============================================================================

  /**
   * Check if mobile device
   * @returns {boolean}
   */
  const isMobile = useCallback(() => {
    return window.innerWidth < 768;
  }, []);

  /**
   * Check if using drag-and-drop mode
   * @returns {boolean}
   */
  const isDragMode = useCallback(() => {
    return settings.mobileInteractionMode === 'drag';
  }, [settings.mobileInteractionMode]);

  /**
   * Check if using tap-to-move mode
   * @returns {boolean}
   */
  const isTapMode = useCallback(() => {
    return settings.mobileInteractionMode === 'tap';
  }, [settings.mobileInteractionMode]);

  // ============================================================================
  // PREMIUM HELPERS
  // ============================================================================

  /**
   * [DEPRECATED]: Old mock premium check — use AuthContext.isPremium instead
   * [KEPT]: For backwards compatibility with any remaining callers
   * [WHY]: Real premium is Stripe-backed via AuthContext, not localStorage settings
   */
  const isPremium = useCallback(() => {
    // [PHASE B / #068 — 2026-05-07]: Same dev-build branch, now via
    //   the canonical `IS_DEV_BUILD` constant.
    if (IS_DEV_BUILD) {
      return settings.isPremium || settings.debugMode;
    }
    return false; // In production, only AuthContext.isPremium is authoritative
  }, [settings.isPremium, settings.debugMode]);

  /**
   * Enable premium features
   */
  const enablePremium = useCallback(() => {
    updateSetting('isPremium', true);
  }, [updateSetting]);

  /**
   * Disable premium features
   */
  const disablePremium = useCallback(() => {
    updateSetting('isPremium', false);
  }, [updateSetting]);

  // ============================================================================
  // CHARACTER MANAGEMENT
  // ============================================================================

  /**
   * Add a saved character
   * @param {Object} character - Character data to save
   */
  const addSavedCharacter = useCallback((character) => {
    setSavedCharacters(prev => {
      // Check if character already exists
      const existingIndex = prev.findIndex(c => c.id === character.id);
      if (existingIndex !== -1) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = character;
        return updated;
      }
      // Add new
      return [...prev, character];
    });
  }, []);

  /**
   * Remove a saved character
   * @param {string} characterId - ID of character to remove
   */
  const removeSavedCharacter = useCallback((characterId) => {
    setSavedCharacters(prev => prev.filter(c => c.id !== characterId));
  }, []);

  /**
   * Clear all saved characters
   */
  const clearSavedCharacters = useCallback(() => {
    setSavedCharacters([]);
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  // [FIX APR 2026 — AUDIT C-4]: Memoize context value to prevent unnecessary consumer re-renders
  const value = useMemo(() => ({
    // Core Settings
    settings,
    setSettings,

    // Session & Characters
    savedCharacters,
    setSavedCharacters,
    sessionStartTime,
    setSessionStartTime,

    // Quick Bonus Presets
    hiddenPresets,
    setHiddenPresets,
    customBonus,
    setCustomBonus,

    // Settings Actions
    updateSetting,
    updateSettings,
    resetSettings,
    toggleSetting,
    getSetting,

    // Display Mode Helpers
    isSimpleMode,
    isJustInventoryMode,
    isDefaultMode,

    // Mobile Helpers
    isMobile,
    isDragMode,
    isTapMode,

    // Premium Helpers
    isPremium,
    enablePremium,
    disablePremium,

    // Character Management
    addSavedCharacter,
    removeSavedCharacter,
    clearSavedCharacters
  }), [
    settings,
    setSettings,
    savedCharacters,
    setSavedCharacters,
    sessionStartTime,
    setSessionStartTime,
    hiddenPresets,
    setHiddenPresets,
    customBonus,
    setCustomBonus,
    updateSetting,
    updateSettings,
    resetSettings,
    toggleSetting,
    getSetting,
    isSimpleMode,
    isJustInventoryMode,
    isDefaultMode,
    isMobile,
    isDragMode,
    isTapMode,
    isPremium,
    enablePremium,
    disablePremium,
    addSavedCharacter,
    removeSavedCharacter,
    clearSavedCharacters
  ]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Hook to access SettingsContext
 * @throws {Error} if used outside SettingsProvider
 *
 * [FIX - NOV 3, 2025]: Enhanced error message with debugging hints
 */
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    console.error('❌ useSettings() called outside SettingsProvider!');
    console.error('📍 Check that your component is wrapped in <AppProviders>');
    console.error('📍 File: src/contexts/SettingsContext.js');
    throw new Error(
      'useSettings must be used within SettingsProvider. ' +
      'Ensure your component is wrapped in <AppProviders>. ' +
      'See src/contexts/SettingsContext.js:411'
    );
  }
  return context;
};

export default SettingsContext;
